import { Script } from "gatsby"
import { useEffect, useState } from "react"
import { Box, Stack, Typography, Container } from "@mui/material"
import type { FC, ResponsiveImageType } from ".."
import { ResponsiveImage, WYSIWYG, } from ".."

type AdLandingComponentProps = {
  title: string
  body: string
  photo: ResponsiveImageType
}

const AdLandingComponent: FC<AdLandingComponentProps> = ({ title, body, photo }) => {
  const [shouldDisplay, setShouldDisplay] = useState<boolean>(false)

  // Check if the component should be displayed based on query param and referrer
  useEffect(() => {
    // Check for UTM parameters
    const urlParams = new URLSearchParams(window.location.search)
    const hasUtmSource = urlParams.has("utm_source")
    const hasUtmCampaign = urlParams.has("utm_campaign")

    // Display component if either UTM parameter exists
    setShouldDisplay(hasUtmSource || hasUtmCampaign)
  }, [])

  // Don't render anything if conditions aren't met
  if (!shouldDisplay) return null

  return (
    <Container maxWidth={false} disableGutters>
      <Box
        position="relative"
        px={{ xs: 2, sm: 5, md: 10 }}
        py={{ xs: 8, md: 5 }}
        pb={{ xs: 20, md: 10 }}
        maxWidth={1520}
        margin="0 auto"
      >
        <Box
          position="relative"
          sx={{
            ml: { xs: 0, md: 8 },
            mt: { xs: 0, md: 0 },
            maxHeight: { xs: "unset", md: 598 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            position="relative"
            width="100%"
            overflow="hidden"
            borderRadius={2}
            sx={{
              aspectRatio: { xs: "4/3", md: "16/9" },
              maxHeight: { xs: 400, md: 598 },
              maxWidth: 1064,
              height: "100%",
              width: "100%",
              transition: "all 0.3s ease-in-out",
              marginLeft: "auto",
              mb: { xs: 20, md: 0 },
            }}
          >
            <ResponsiveImage
              image={photo}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          <Box
            sx={{
              position: { xs: "relative", md: "absolute" },
              bottom: { xs: "unset", md: 40 },
              left: { xs: "unset", md: -70, xl: 40 },
              right: { xs: "unset", md: "unset" },
              top: { xs: -180, md: "unset" },
              maxWidth: { xs: "100%", md: 481 },
              width: { xs: "calc(100% - 24px)", md: "auto" },
              bgcolor: (theme) => theme.palette["secondary.200"].main,
              transition: "all 0.3s ease-in-out",
              p: { xs: 6, sm: 8, md: 10 },
              borderRadius: 1.5,
              boxShadow: (theme) =>
                `0 0 8px 0 ${
                  theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)"
                }`,
              zIndex: 1,
            }}
          >
            <Stack>
              <Typography
                variant="heading.lg"
                component="h2"
                fontWeight={700}
                sx={{
                  color: (theme) => theme.palette["neutral.700"].main,
                }}
              >
                {title}
              </Typography>
              <WYSIWYG
                content={body}
                sx={(theme) => ({
                  "& ul": {
                    marginTop: theme.spacing(8),
                  },
                  "& ul li": {
                    marginBottom: theme.spacing(3),
                    paddingLeft: theme.spacing(2),
                    fontSize: "15px",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "170%",
                    letterSpacing: "-0.18px",
                  },
                  "& ul li::marker": {
                    content:
                      "url(data:image/svg+xml;base64,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)",
                  },
                })}
              />
              <Box
                zIndex={3}
                mt={{ xs: 8, lg: 8 }}
                left={(theme) => theme.spacing(3)}
                bottom={(theme) => theme.spacing(3)}
                sx={(theme) => ({
                  fontSize: "0.75rem!important",
                  background: theme.palette["neutral.700"].main,
                  borderRadius: 1,
                  backdropFilter: "blur(4px)",
                  padding: theme.spacing(3, 0),
                  maxWidth: { xs: "100%", md: "80%" },
                })}
              >
                <TrustPilotPromoWidget />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

const TrustPilotPromoWidget = () => {
  return (
    <>
      <Script src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async />
      <Box>
        <Container>
          <Box>
            <div
              className="trustpilot-widget"
              data-locale="en-US"
              data-template-id="5419b6a8b0d04a076446a9ad"
              data-businessunit-id="547cf0db00006400057be14e"
              data-style-height="24px"
              data-style-width="100%"
              data-theme="dark"
              data-style-alignment="center"
            >
              <a
                href="https://www.trustpilot.com/review/goway.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Trustpilot
              </a>
            </div>
          </Box>
        </Container>
      </Box>
    </>
  )
}

export default AdLandingComponent
